/**
 * The recorder: turns a live session into its replayable record, grading every take through
 * the engine as it goes.
 *
 * A surface that records a take grades through the recorder, so what is graded live and what
 * is recorded are the same calls. Everything that enters is copied as plain data first (a JSON
 * round trip), and the engine is fed that copy: the live judgments are made from exactly what
 * a replay will read. The invariant, checked by the specs: at any moment, replaying the record
 * gives the live judged take.
 *
 * Pure: every function returns a new state.
 */

import {
  CapabilityMask,
  JudgmentConfig,
  TakeSetup,
  TakeState,
  applyEvent,
  advanceTo,
  finishTake,
  readTake,
  startTake,
} from '../grader';
import { JudgedTake } from '../judgedTake';
import { ScoringConfig, scoringErrors } from '../preset';
import {
  Dimension,
  GradingConfigError,
  Hand,
  PedalKind,
  PerformanceEvent,
  RealizedScore,
} from '../types';
import { GRADING_ENGINE_VERSION } from '../version';
import { ClockSpec, clockFromSpec, fastestBpm, slowestBpm, withTempoChange } from './clockSpec';
import {
  ASSISTS,
  AssistLevels,
  AssistName,
  HandRole,
  StampContext,
  TakeStamp,
  gradedDimensionsOf,
  gradedHandsOf,
  mergeAssists,
  validateLevel,
  validateStampContext,
} from './stamp';
import {
  BeatRange,
  MAX_SEGMENT_EVENTS,
  Material,
  RecordedTake,
  RecordedTakeSetup,
  STREAM_FORMAT_VERSION,
  KEY_SIGNATURE_DISPLAYS,
  Segment,
  SessionHeader,
  SessionRecord,
  SettingChange,
  StreamEvent,
  TakeDisplay,
  TakeEndReason,
  TempoSource,
} from './stream';
import { takeResult } from './takeResult';

/** The settings in force, as the session's own events set them. */
interface InForce {
  readonly offTheRecord: boolean;
  readonly assists: AssistLevels;
}

interface OpenTake {
  readonly takeId: string;
  readonly materialId: string;
  /** What the surface said about the take when it began. */
  readonly context: StampContext;
  /** The tempo map now (the setup keeps the one the take began with). */
  readonly clock: ClockSpec | null;
  readonly engineSetup: TakeSetup;
  readonly engine: TakeState;
  /** The performance events folded so far, for re-folding after a tempo change. */
  readonly folded: readonly PerformanceEvent[];
  /** The easiest conditions in force at any moment of the take so far. */
  readonly assists: AssistLevels;
  readonly offTheRecord: boolean;
}

export interface RecorderState {
  readonly record: SessionRecord;
  /** The segment still being filled; sealed into the record on a cut or `sealSegment`. */
  readonly tail: Segment | null;
  readonly nextSeq: number;
  /** How far the session clock has run: events must not come before it. */
  readonly nowMs: number;
  readonly inForce: InForce;
  readonly open: OpenTake | null;
  /** The judged take of every ended take, by take id. */
  readonly judged: Readonly<Record<string, JudgedTake>>;
}

/** Plain data: what a replay will read back. Throws when something would not survive. */
function asData<T>(value: T, what: string): T {
  const text = JSON.stringify(value);
  const copy: unknown = text === undefined ? undefined : JSON.parse(text);
  if (!sameData(value, copy)) {
    throw new GradingConfigError(`${what} must be plain data (no functions or non-finite numbers)`);
  }
  return copy as T;
}

/** True when the copy carries the original exactly (minus-zero becomes zero, which is the point). */
function sameData(original: unknown, copy: unknown): boolean {
  if (typeof original === 'number') {
    return Number.isFinite(original) && original === copy;
  }
  if (original === null || typeof original !== 'object') {
    return original === copy;
  }
  if (Array.isArray(original)) {
    return (
      Array.isArray(copy) &&
      original.length === copy.length &&
      original.every((item, index) => sameData(item, copy[index]))
    );
  }
  const prototype = Object.getPrototypeOf(original);
  if ((prototype !== Object.prototype && prototype !== null) || copy === null) {
    return false;
  }
  const source = original as Record<string, unknown>;
  const target = copy as Record<string, unknown>;
  return Object.keys(source).every((key) =>
    source[key] === undefined ? !(key in target) : sameData(source[key], target[key]),
  );
}

export interface SessionStart {
  readonly sessionId: string;
  readonly surface: SessionHeader['surface'];
  readonly surfaceSessionId?: string | null;
  readonly startedAt: string;
  readonly appVersion?: string | null;
  readonly offTheRecord?: boolean;
}

export function startSession(start: SessionStart): RecorderState {
  if (start.surface === 'tutorial' && (start.surfaceSessionId ?? null) !== null) {
    throw new GradingConfigError('a tutorial session has no play-session row of its own');
  }
  const header: SessionHeader = asData(
    {
      sessionId: start.sessionId,
      surface: start.surface,
      surfaceSessionId: start.surfaceSessionId ?? null,
      startedAt: start.startedAt,
      formatVersion: STREAM_FORMAT_VERSION,
      appVersion: start.appVersion ?? null,
      offTheRecord: start.offTheRecord ?? false,
    },
    'the session header',
  );
  return {
    record: { header, materials: [], takes: [], segments: [] },
    tail: null,
    nextSeq: 0,
    nowMs: 0,
    inForce: { offTheRecord: header.offTheRecord, assists: {} },
    open: null,
    judged: {},
  };
}

/**
 * Links the session to its surface's own play-session row once that row exists (a session can
 * start, or be recorded offline, before it does). Throws when the session is already linked to
 * another row, or the surface has none.
 */
export function linkPlaySession(state: RecorderState, surfaceSessionId: string): RecorderState {
  const header = state.record.header;
  if (header.surface === 'tutorial') {
    throw new GradingConfigError('a tutorial session has no play-session row of its own');
  }
  if (header.surfaceSessionId !== null && header.surfaceSessionId !== surfaceSessionId) {
    throw new GradingConfigError(`the session is already linked to ${header.surfaceSessionId}`);
  }
  return {
    ...state,
    record: { ...state.record, header: { ...header, surfaceSessionId } },
  };
}

function checkTime(state: RecorderState, timeMs: number): void {
  if (!Number.isFinite(timeMs) || timeMs < state.nowMs) {
    throw new GradingConfigError(
      `session events must arrive in time order (${timeMs} < ${state.nowMs})`,
    );
  }
}

/** Seals the segment being filled, if any: it is then final and can be sent. */
export function sealSegment(state: RecorderState): RecorderState {
  if (state.tail === null) {
    return state;
  }
  return {
    ...state,
    record: { ...state.record, segments: [...state.record.segments, state.tail] },
    tail: null,
  };
}

/** Appends one event to the segment being filled, cutting a new segment where one must start. */
function appendToStream(state: RecorderState, event: StreamEvent): RecorderState {
  const takeId = state.open ? state.open.takeId : null;
  const offTheRecord = state.inForce.offTheRecord;
  const fits =
    state.tail !== null &&
    state.tail.takeId === takeId &&
    state.tail.offTheRecord === offTheRecord &&
    state.tail.events.length < MAX_SEGMENT_EVENTS;
  if (fits && state.tail) {
    return { ...state, tail: { ...state.tail, events: [...state.tail.events, event] } };
  }
  const sealed = sealSegment(state);
  return {
    ...sealed,
    tail: { seq: sealed.nextSeq, takeId, offTheRecord, events: [event] },
    nextSeq: sealed.nextSeq + 1,
  };
}

export interface MaterialInput {
  readonly materialId: string;
  /** Sight reading: the generated phrase the score was realized from. */
  readonly phraseId?: number | null;
  readonly score: RealizedScore;
}

export function addMaterial(
  state: RecorderState,
  timeMs: number,
  input: MaterialInput,
): RecorderState {
  checkTime(state, timeMs);
  const phraseId = input.phraseId ?? null;
  if (phraseId !== null && state.record.header.surface !== 'sight-reading') {
    throw new GradingConfigError('a phrase id belongs to a sight-reading material only');
  }
  if (phraseId !== null && !(Number.isInteger(phraseId) && phraseId >= 1)) {
    throw new GradingConfigError(`a phrase id is a positive whole number, got ${phraseId}`);
  }
  if (state.record.materials.some((material) => material.materialId === input.materialId)) {
    throw new GradingConfigError(`material ${input.materialId} is already in the session`);
  }
  const material: Material = asData(
    { materialId: input.materialId, phraseId, atMs: timeMs, score: input.score },
    `material ${input.materialId}`,
  );
  return {
    ...state,
    nowMs: timeMs,
    record: { ...state.record, materials: [...state.record.materials, material] },
  };
}

/** The part of a material a take grades. */
export function scoreForTake(score: RealizedScore, range: BeatRange | null): RealizedScore {
  if (range === null) {
    return score;
  }
  const inRange = (beat: number) => beat >= range.fromBeat && beat < range.toBeat;
  return {
    ...score,
    notes: score.notes.filter((written) => inRange(written.onsetBeat)),
    ...(score.pedalMarks
      ? { pedalMarks: score.pedalMarks.filter((mark) => inRange(mark.downBeat)) }
      : {}),
  };
}

/** The engine's setup for a recorded take, with the clock it has now. */
export function engineSetupFor(
  setup: RecordedTakeSetup,
  score: RealizedScore,
  hands: Readonly<Record<Hand, HandRole>>,
  clock: ClockSpec | null,
): TakeSetup {
  return {
    score: scoreForTake(score, setup.beatRange),
    judgment: setup.judgment,
    clock: clock ? clockFromSpec(clock) : null,
    referenceTempoBpm: setup.referenceTempoBpm,
    gradedHands: gradedHandsOf(hands),
    mask: setup.mask,
    narrowedAway: setup.narrowedAway,
  };
}

function buildStamp(context: StampContext, open: OpenTake, engine: TakeState): TakeStamp {
  return {
    cell: context.cell,
    frontier: {
      tempoBpm: open.clock ? slowestBpm(open.clock) : null,
      hands: context.hands,
      graded: gradedDimensionsOf(engine),
      assists: open.assists,
      tierNumber: context.tierNumber ?? null,
      rangeOctaves: context.exercise?.rangeOctaves ?? null,
      direction: context.exercise?.direction ?? null,
      rhythmDensity: context.exercise?.rhythmDensity ?? null,
    },
    preset: context.preset,
    input: context.input,
    tempo: {
      slowestBpm: open.clock ? slowestBpm(open.clock) : null,
      fastestBpm: open.clock ? fastestBpm(open.clock) : null,
      goalBpm: context.goalTempoBpm ?? null,
    },
    engineVersion: GRADING_ENGINE_VERSION,
    offTheRecord: open.offTheRecord,
  };
}

export interface TakeBegin {
  readonly takeId: string;
  readonly materialId: string;
  readonly beatRange?: BeatRange | null;
  readonly judgment: JudgmentConfig;
  /** The take's scoring (layers 2 and 3), for its result. Recorded with the setup. */
  readonly scoring: ScoringConfig;
  readonly clock: ClockSpec | null;
  readonly referenceTempoBpm?: number | null;
  readonly mask?: CapabilityMask;
  readonly narrowedAway?: readonly Dimension[];
  readonly context: StampContext;
  /** How the material is shown during the take: recorded with the setup, never graded. */
  readonly display?: TakeDisplay;
}

/** Every problem with a take's display facts; empty when valid. */
function displayErrors(display: TakeDisplay): string[] {
  const known: readonly string[] = ['keySignature'];
  return [
    ...Object.keys(display)
      .filter((name) => known.indexOf(name) < 0)
      .map((name) => `unknown display fact "${name}"`),
    ...(display.keySignature === undefined ||
    KEY_SIGNATURE_DISPLAYS.indexOf(display.keySignature) >= 0
      ? []
      : [`the key signature display is one of ${KEY_SIGNATURE_DISPLAYS.join(', ')}`]),
  ];
}

/**
 * Begins a take. Throws {@link GradingConfigError} when a take is already open, the material is
 * unknown, or the setup or stamp context is invalid.
 */
export function beginTake(state: RecorderState, timeMs: number, begin: TakeBegin): RecorderState {
  checkTime(state, timeMs);
  if (state.open) {
    throw new GradingConfigError(`take ${state.open.takeId} is still open; end it first`);
  }
  if (state.record.takes.some((take) => take.takeId === begin.takeId)) {
    throw new GradingConfigError(`take ${begin.takeId} is already in the session`);
  }
  const material = state.record.materials.find(
    (candidate) => candidate.materialId === begin.materialId,
  );
  if (!material) {
    throw new GradingConfigError(`material ${begin.materialId} is not in the session`);
  }
  const context = asData(begin.context, 'the stamp context');
  validateStampContext(context, state.record.header.surface);
  const displayProblems = begin.display ? displayErrors(begin.display) : [];
  if (displayProblems.length > 0) {
    throw new GradingConfigError(`take ${begin.takeId} display: ${displayProblems.join('; ')}`);
  }
  const scoringProblems = scoringErrors(begin.scoring);
  if (scoringProblems.length > 0) {
    throw new GradingConfigError(`take ${begin.takeId} scoring: ${scoringProblems.join('; ')}`);
  }
  const setup: RecordedTakeSetup = asData(
    {
      beatRange: begin.beatRange ?? null,
      judgment: begin.judgment,
      scoring: begin.scoring,
      clock: begin.clock,
      referenceTempoBpm: begin.referenceTempoBpm ?? null,
      mask: begin.mask ?? { undetectable: [] },
      narrowedAway: begin.narrowedAway ?? [],
      ...(begin.display ? { display: begin.display } : {}),
    },
    `take ${begin.takeId} setup`,
  );
  const engineSetup = engineSetupFor(setup, material.score, context.hands, setup.clock);
  const engine = startTake(engineSetup);
  const sealed = sealSegment(state);
  const open: OpenTake = {
    takeId: begin.takeId,
    materialId: begin.materialId,
    context,
    clock: setup.clock,
    engineSetup,
    engine,
    folded: [],
    assists: sealed.inForce.assists,
    offTheRecord: sealed.inForce.offTheRecord,
  };
  const take: RecordedTake = {
    takeId: begin.takeId,
    takeIndex: sealed.record.takes.length,
    startMs: timeMs,
    firstSeq: sealed.nextSeq,
    materialId: begin.materialId,
    setup,
    stampAtStart: buildStamp(context, open, engine),
    end: null,
  };
  return {
    ...sealed,
    nowMs: timeMs,
    open,
    record: { ...sealed.record, takes: [...sealed.record.takes, take] },
  };
}

const PEDALS: readonly PedalKind[] = ['sustain', 'sostenuto', 'soft'];

const isMidi = (value: number) => Number.isInteger(value) && value >= 0 && value <= 127;

/**
 * What the backend refuses in a stored event, checked here so a bad value fails at the event,
 * not later as a refused body (which would drop every segment in it).
 */
function performanceEventErrors(event: PerformanceEvent): string[] {
  switch (event.kind) {
    case 'press':
      return isMidi(event.pitch) &&
        Number.isFinite(event.velocity) &&
        event.velocity >= 0 &&
        event.velocity <= 127 &&
        (event.finger === undefined ||
          (Number.isInteger(event.finger) && event.finger >= 1 && event.finger <= 5))
        ? []
        : ['a press needs a MIDI pitch (0-127), a velocity 0-127 and an optional finger 1-5'];
    case 'release':
      return isMidi(event.pitch) ? [] : ['a release needs a MIDI pitch (0-127)'];
    case 'pedal':
      return PEDALS.indexOf(event.pedal) >= 0 && isMidi(event.value)
        ? []
        : [`a pedal change needs one of ${PEDALS.join(', ')} and a whole value 0-127`];
    case 'scope-reset':
      return [];
    default:
      return ['unknown performance event'];
  }
}

/** A key press, release, pedal change or scope reset. Inside a take it is graded. */
export function recordEvent(state: RecorderState, event: PerformanceEvent): RecorderState {
  checkTime(state, event.timeMs);
  const copy = asData(event, 'a performance event');
  const problems = performanceEventErrors(copy);
  if (problems.length > 0) {
    throw new GradingConfigError(problems.join('; '));
  }
  const streamed = { ...appendToStream(state, copy), nowMs: copy.timeMs };
  if (!state.open) {
    return streamed;
  }
  return {
    ...streamed,
    open: {
      ...state.open,
      engine: applyEvent(state.open.engine, copy),
      folded: [...state.open.folded, copy],
    },
  };
}

/**
 * A tempo change. Inside a timed take it extends the take's tempo map and the take is graded
 * again under it (the take keeps going and is stamped at its slowest tempo).
 */
export function recordTempo(
  state: RecorderState,
  timeMs: number,
  bpm: number,
  source: TempoSource,
): RecorderState {
  checkTime(state, timeMs);
  const event = asData({ kind: 'tempo' as const, timeMs, bpm, source }, 'a tempo event');
  const open = state.open;
  if (!open || open.clock === null) {
    if (!(bpm > 0)) {
      throw new GradingConfigError(
        `tempo must be a positive number of beats per minute, got ${bpm}`,
      );
    }
    return { ...appendToStream(state, event), nowMs: timeMs };
  }
  const clock = withTempoChange(open.clock, timeMs, bpm);
  const take = state.record.takes[state.record.takes.length - 1];
  const material = state.record.materials.find(
    (candidate) => candidate.materialId === open.materialId,
  ) as Material;
  const engineSetup = engineSetupFor(take.setup, material.score, open.context.hands, clock);
  const engine = open.folded.reduce(applyEvent, startTake(engineSetup));
  return {
    ...appendToStream(state, event),
    nowMs: timeMs,
    open: { ...open, clock, engineSetup, engine },
  };
}

export function recordCursor(
  state: RecorderState,
  timeMs: number,
  beat: number,
  cause: 'start' | 'seek' | 'loop' | 'hold',
): RecorderState {
  checkTime(state, timeMs);
  const event = asData({ kind: 'cursor' as const, timeMs, beat, cause }, 'a cursor event');
  return { ...appendToStream(state, event), nowMs: timeMs };
}

function inForceAfter(inForce: InForce, change: SettingChange): InForce {
  switch (change.name) {
    case 'assist':
      return {
        ...inForce,
        assists: ASSISTS.filter(
          (name) => name !== change.assist && inForce.assists[name] !== undefined,
        ).reduce<Partial<Record<AssistName, number>>>(
          (kept, name) => ({ ...kept, [name]: inForce.assists[name] }),
          change.level > 0 ? { [change.assist]: change.level } : {},
        ),
      };
    case 'off-the-record':
      return { ...inForce, offTheRecord: change.on };
  }
}

function validateSetting(change: SettingChange): void {
  if (change.name === 'assist') {
    validateLevel(change.name, change.level);
  }
  if (change.name === 'assist' && ASSISTS.indexOf(change.assist) < 0) {
    throw new GradingConfigError(`assist: one of ${ASSISTS.join(', ')}, got ${change.assist}`);
  }
}

/**
 * A settings change. Assists and off-the-record feed the stamp: a take is stamped with the most
 * help at any moment of it, and is off the record if it was off at any moment. Changes the engine fixes for a whole take (hands, narrowing, the
 * input, advancement, the preset, anything that changes the notes) are not settings events: end
 * the take and begin the next one.
 */
export function recordSetting(
  state: RecorderState,
  timeMs: number,
  change: SettingChange,
): RecorderState {
  checkTime(state, timeMs);
  const copy = asData(change, 'a setting');
  validateSetting(copy);
  const inForce = inForceAfter(state.inForce, copy);
  const streamed = {
    ...appendToStream({ ...state, inForce }, { kind: 'setting', timeMs, change: copy }),
    nowMs: timeMs,
  };
  if (!state.open) {
    return streamed;
  }
  return {
    ...streamed,
    open: {
      ...state.open,
      assists: mergeAssists(state.open.assists, inForce.assists),
      offTheRecord: state.open.offTheRecord || inForce.offTheRecord,
    },
  };
}

/**
 * Lets time pass with no event, for live reads (pending notes become missed as their windows
 * close). Not recorded: a replay does not need it.
 */
export function tick(state: RecorderState, timeMs: number): RecorderState {
  if (!Number.isFinite(timeMs)) {
    throw new GradingConfigError(`a tick needs a finite time, got ${timeMs}`);
  }
  return {
    ...state,
    nowMs: Math.max(state.nowMs, timeMs),
    open: state.open ? { ...state.open, engine: advanceTo(state.open.engine, timeMs) } : null,
  };
}

/** The open take's judged take now; null when no take is open. */
export function readLiveTake(state: RecorderState): JudgedTake | null {
  return state.open ? readTake(state.open.engine) : null;
}

export interface TakeFinish {
  readonly reason: TakeEndReason;
}

/**
 * Ends the open take: nothing is pending any more (notes that came due unplayed are missed). The
 * take's last segment is sealed, and its final stamp and result (under the scoring recorded with
 * its setup) are recorded.
 */
export function endTake(state: RecorderState, timeMs: number, finish: TakeFinish): RecorderState {
  checkTime(state, timeMs);
  const open = state.open;
  if (!open) {
    throw new GradingConfigError('no take is open');
  }
  const engine = finishTake(open.engine, timeMs);
  const judged = readTake(engine);
  const sealed = sealSegment(state);
  const takes = sealed.record.takes;
  const take = takes[takes.length - 1];
  const segments = sealed.record.segments.filter((segment) => segment.takeId === take.takeId);
  const ended: RecordedTake = {
    ...take,
    end: {
      endMs: timeMs,
      reason: finish.reason,
      stamp: buildStamp(open.context, open, engine),
      result: takeResult(judged, take.setup.scoring),
      segmentCount: segments.length,
      eventCount: segments.reduce((total, segment) => total + segment.events.length, 0),
    },
  };
  return {
    ...sealed,
    nowMs: timeMs,
    open: null,
    record: { ...sealed.record, takes: [...takes.slice(0, -1), ended] },
    judged: { ...sealed.judged, [take.takeId]: judged },
  };
}
